import type { MutableRefObject, RefObject } from 'react';
import type { WorkbookInstance } from '@fortune-sheet/react';
import type { FortuneCell, FortuneSheet } from './types';
import { batchCallApisQuietly } from './excel/workbookInternals';
import type { ApiCall } from './excel/workbookInternals';

// Fortune-Sheet evaluates each cell once, row-major and tab by tab, without
// resolving dependencies first — so a cross-sheet reference into a not-yet-
// computed cell reads stale. Re-running until values settle fixes that; the cap
// keeps a circular reference from looping forever.
export const MAX_RECALC_PASSES = 10;

// The workbook's state is a React state update: a pass is only visible on a FRESH
// workbookRef.current after React commits it, so each pass waits this long.
export const RECALC_PASS_WAIT_MS = 30;

/** A formula cell that arrived without a cached value, plus the number format the
 *  importer gave it (the first recalc passes can overwrite that format — see restoreFormats). */
export interface UncachedFormula {
    sheetId: string;
    r: number;
    c: number;
    ct?: FortuneCell['ct'];
}

/** Formula cells with no cached value (e.g. openpyxl-written files, which expect the
 *  opening app to recalculate). Files saved by Excel carry cached values → empty list. */
export const findUncachedFormulas = (sheets: FortuneSheet[] | null | undefined): UncachedFormula[] =>
    (sheets ?? []).flatMap((sheet) =>
        (sheet.celldata ?? [])
            .filter((cell) => cell.v?.f && cell.v.v == null)
            .map((cell) => ({ sheetId: String(sheet.id), r: cell.r, c: cell.c, ct: cell.v?.ct })),
    );

// suppressDirtyRef is shared by every writer that must not count as an edit
// (stub hydration, recalc loops). Holds are counted so one writer finishing can
// never release the flag while another still has ops in flight.
const holdCounts = new WeakMap<MutableRefObject<boolean>, number>();

/** Mark the workbook as "not being edited" until the returned release is called (idempotent). */
export const holdDirtySuppression = (ref: MutableRefObject<boolean>): (() => void) => {
    holdCounts.set(ref, (holdCounts.get(ref) ?? 0) + 1);
    ref.current = true;
    let released = false;
    return () => {
        if (released) return;
        released = true;
        const remaining = (holdCounts.get(ref) ?? 1) - 1;
        holdCounts.set(ref, remaining);
        if (remaining <= 0) ref.current = false;
    };
};

type SheetDataById = Map<string, FortuneSheet['data']>;

const sheetDataById = (workbook: WorkbookInstance): SheetDataById =>
    new Map(workbook.getAllSheets().map((sheet) => [String(sheet.id), sheet.data]));

/** Consecutive uncached columns in a row collapse into one range, so a range never
 *  contains a formula that arrived with a cached value. */
const rangesBySheet = (uncached: UncachedFormula[]) => {
    const bySheet = new Map<string, UncachedFormula[]>();
    uncached.forEach((cell) => {
        const cells = bySheet.get(cell.sheetId) ?? [];
        cells.push(cell);
        bySheet.set(cell.sheetId, cells);
    });
    return new Map(
        Array.from(bySheet, ([sheetId, cells]) => {
            const ranges: { row: [number, number]; column: [number, number] }[] = [];
            [...cells]
                .sort((a, b) => a.r - b.r || a.c - b.c)
                .forEach(({ r, c }) => {
                    const last = ranges[ranges.length - 1];
                    if (last && last.row[0] === r && last.column[1] === c - 1) last.column[1] = c;
                    else ranges.push({ row: [r, r], column: [c, c] });
                });
            return [sheetId, ranges] as const;
        }),
    );
};

/**
 * One pass, queued as a single batch (one state update): Fortune-Sheet's
 * calculateFormula(id, range) evaluates in sheet `id` but WRITES the results into
 * whichever sheet is current, so each sheet is made current first, and the original
 * tab is restored at the end.
 */
const calculatePass = (workbook: WorkbookInstance, ranges: ReturnType<typeof rangesBySheet>, originalSheetId?: string): void => {
    const data = sheetDataById(workbook);
    const calls: ApiCall[] = [];
    ranges.forEach((sheetRanges, sheetId) => {
        if (!data.has(sheetId)) return;
        calls.push({ name: 'activateSheet', args: [{ id: sheetId }] });
        sheetRanges.forEach((range) => calls.push({ name: 'calculateFormula', args: [sheetId, range] }));
    });
    calls.push({ name: 'activateSheet', args: [{ id: originalSheetId }] });
    batchCallApisQuietly(workbook, calls);
};

/**
 * A formula evaluated before its dependencies reads them as TRUE, and Fortune-Sheet
 * then overwrites the cell's number format with a General boolean one — permanently,
 * even once the value is right. Put the importer's format back, but never onto a
 * cell that still holds a boolean: that would show a bogus 1 or 100% as if it were a
 * real number. Returns whether anything was queued.
 */
const restoreFormats = (workbook: WorkbookInstance, uncached: UncachedFormula[]): boolean => {
    const data = sheetDataById(workbook);
    const calls = uncached
        .filter(({ sheetId, r, c, ct }) => {
            const live = data.get(sheetId)?.[r]?.[c];
            return ct && typeof live?.v !== 'boolean' && (live?.ct?.fa !== ct.fa || live?.ct?.t !== ct.t);
        })
        .map(({ sheetId, r, c, ct }) => ({ name: 'setCellFormat', args: [r, c, 'ct', ct, { id: sheetId }] }));
    if (!calls.length) return false;
    // setCellFormat leaves the workbook-level config pointing at the last sheet it
    // touched; a no-op row-height write resyncs it to the (unchanged) current sheet.
    batchCallApisQuietly(workbook, [...calls, { name: 'setRowHeight', args: [{}] }]);
    return true;
};

interface RecalcArgs {
    workbookRef: RefObject<WorkbookInstance>;
    uncached: UncachedFormula[];
    /** Held while updates are in flight: they emit onOp like a real edit, and
     *  opening a file must not mark it dirty. */
    suppressDirtyRef: MutableRefObject<boolean>;
    /** True once the workbook this recalc was started for is gone/replaced. */
    isStale: () => boolean;
}

/**
 * Recalculate the uncached formulas, pass after pass, until none changes (or
 * MAX_RECALC_PASSES). Formulas that arrived with a cached value are never touched.
 */
export const recalculateUntilStable = ({ workbookRef, uncached, suppressDirtyRef, isStale }: RecalcArgs): void => {
    const ranges = rangesBySheet(uncached);
    const release = holdDirtySuppression(suppressDirtyRef);
    let passes = 0;
    let waitedForWorkbook = false;
    let originalSheetId: string | undefined;
    let previous: unknown[] | null = null;

    const runPassUnsafe = () => {
        const workbook = workbookRef.current;
        if (isStale()) return release();
        if (!workbook) {
            // The workbook may not have exposed its API yet: one more tick, then give up.
            if (passes === 0 && !waitedForWorkbook) {
                waitedForWorkbook = true;
                setTimeout(runPass, RECALC_PASS_WAIT_MS);
                return;
            }
            return release();
        }
        const data = sheetDataById(workbook);
        const values = uncached.map(({ sheetId, r, c }) => data.get(sheetId)?.[r]?.[c]?.v);
        if ((previous && values.every((value, i) => value === previous![i])) || passes >= MAX_RECALC_PASSES) {
            if (restoreFormats(workbook, uncached)) setTimeout(release, RECALC_PASS_WAIT_MS);
            else release();
            return;
        }
        if (passes === 0) originalSheetId = workbook.getSheet()?.id;
        previous = values;
        passes += 1;
        calculatePass(workbook, ranges, originalSheetId);
        setTimeout(runPass, RECALC_PASS_WAIT_MS);
    };
    // A throw must not end the timer chain with the dirty suppression still held.
    const runPass = () => {
        try {
            runPassUnsafe();
        } catch (error) {
            console.warn('[SheetEditor] formula recalc aborted', error);
            release();
        }
    };
    runPass();
};
